import { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";

import { getUsersByClient } from "../../../../actions/usersActions";
import { addUsersToGroup, getUsersByGroup } from "../../../../actions/manageGroupsActions";
import { closeLoading, openLoading } from "../../../../components/loadingModal/loading";

import addUserIcon from "../../../../assets/icons/AddUsersIcon.png";
import addUserIconBlue from "../../../../assets/icons/add_user_darkBlue.png";
import alert from "../../../../components/AlertModal/alertModal";
import Button from '../../../../components/Button';
import Table from "../../../../components/Table";

import styles from "./assign_users.module.css";

const AssignUsers = ({
    closeModal,
    usersList,
    currentUsers,
    getUsers,
    addUsersToGroup,
    getUsersByGroup,
    newGroup
}) => {
    const [usersSelected, setUsersSelected] = useState({});
    const [availableUsers, setAvailableUsers] = useState([]);
    const { groupId } = useParams();

    // Memoized table columns
    const columns = useMemo(() => [
        {
            accessorKey: 'username',
            header: 'username',
            Header: () => <div className="table-title">USERNAME</div>,
            size: 200,
            cell: (info) => <div className={styles.usernameCell}>{info.getValue()}</div>
        },
        {
            accessorKey: 'firstname',
            header: 'firstname',
            Header: () => <div className="table-title">FIRST NAME</div>,
            filterVariant: 'text',
            size: 200,
        },
        {
            accessorKey: 'lastname',
            header: 'lastname',
            Header: () => <div className="table-title">LAST NAME</div>,
            filterVariant: 'text',
            size: 200,
        },
    ], []);

    // Filter available users
    useEffect(() => {
        const currentUserIds = new Set(currentUsers.map(user => user.idUser));
        const filteredUsers = usersList.filter(user => !currentUserIds.has(user.idUser));
        setAvailableUsers(filteredUsers);
    }, [currentUsers, usersList]);

    // Initialize users
    useEffect(() => {
        const fetchUsers = async () => {
            const order = { column: "firstname", order: "asc" };
            openLoading();
            await getUsers(order);
            closeLoading();
        };

        fetchUsers();
    }, [getUsers]);

    // Event handlers
    const handleCancel = useCallback((e) => {
        e?.preventDefault();
        setUsersSelected({});
        closeModal();
    }, [closeModal]);

    const handleSave = useCallback(async () => {
        if (Object.keys(usersSelected).length === 0) return;

        openLoading();

        try {
            const selectedIds = Object.keys(usersSelected);
            const usersToAdd = usersList.filter(user =>
                selectedIds.includes(`${user.idUser}`)
            );

            const groupParams = groupId
                ? { idGroup: groupId, users: usersToAdd }
                : { users: usersToAdd };

            await addUsersToGroup(groupParams);

            if (groupId || newGroup) {
                await getUsersByGroup(groupId || newGroup, {
                    column: "firstname",
                    order: "asc",
                    atribute: "users"
                });
            }

            setUsersSelected({});
            closeModal();
        } catch (error) {
            alert("Error", error.message);
        } finally {
            closeLoading();
        }
    }, [usersSelected, usersList, groupId, newGroup, addUsersToGroup, getUsersByGroup, closeModal]);

    const hasSelectedUsers = Object.keys(usersSelected).length > 0;
    const hasAvailableUsers = availableUsers.length > 0;

    return (
        <div className={styles.overlay}>
            <div className={styles.modalContainer}>
                <div className={styles.modalContent}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.titleSection}>
                            <h2 className={styles.title}>Add Users to Group</h2>
                            <p className={styles.subtitle}>
                                Select users from the list below to add them to this group
                            </p>
                        </div>
                        <button
                            className={styles.closeButton}
                            onClick={handleCancel}
                            aria-label="Close modal"
                        >
                            ×
                        </button>
                    </div>

                    {/* Stats Bar */}
                    <div className={styles.statsBar}>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{availableUsers.length}</span>
                            <span className={styles.statLabel}>Available Users</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{Object.keys(usersSelected).length}</span>
                            <span className={styles.statLabel}>Selected</span>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className={styles.tableSection}>
                        {hasAvailableUsers ? (
                            <div className={styles.tableWrapper}>
                                <Table
                                    rowId="idUser"
                                    data={availableUsers}
                                    columns={columns}
                                    height="400px"
                                    enableRowSelection
                                    setSelectedRows={setUsersSelected}
                                />
                            </div>
                        ) : (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateIcon}>👥</div>
                                <h3 className={styles.emptyStateTitle}>No Users Available</h3>
                                <p className={styles.emptyStateText}>
                                    All users are already assigned to this group or no users exist in the system.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className={styles.footer}>
                        <Button
                            onClick={handleCancel}
                            label="CANCEL"
                            size="medium"
                            styleButton="normal"
                        />
                        <Button
                            disabled={!hasSelectedUsers}
                            onClick={handleSave}
                            label="ADD SELECTED USERS"
                            size="medium"
                            styleButton="light"
                            image={
                                <img
                                    src={addUserIcon}
                                    alt="Add Users"
                                    className={styles.buttonIcon}
                                />
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Memoized dispatch functions
const mapDispatchToProps = {
    getUsers: (order) => getUsersByClient(order),
    addUsersToGroup: (params) => addUsersToGroup(params),
    getUsersByGroup: (groupId, params) => getUsersByGroup(groupId, params),
};

// Memoized state mapping
const mapStateToProps = (state) => ({
    usersList: state.usersReducer.usersList,
    currentUsers: state.manageGroups.users
});

export default connect(mapStateToProps, mapDispatchToProps)(AssignUsers);